/**
 * The one-question cards for bank rows (product spec section 9). Pure
 * builders returning Swedish copy keyed by entity type where the spec
 * differs. Nothing here books anything; the answers map to a
 * `TransactionInput` built by the UI (`answerToKind` helps).
 */
import { formatSek } from '../money';
import type { Ore } from '../money';
import { formatSwedishDate } from '../periods/deadlines';
import type { BankRow, EntityType, IsoDate, TransactionKind } from '../types';
import type { Candidate } from './match';
import { candidateSummary, findTransferPair, looksLikeOwnTransferText } from './match';

export type QuestionCardKind = 'match' | 'receipt' | 'money_in' | 'transfer' | 'tax' | 'private_warning' | 'combination' | 'refund';

export interface QuestionOption {
  label: string;
  value: string;
}

export interface QuestionCard {
  kind: QuestionCardKind;
  text: string;
  options: QuestionOption[];
}

export const MATCH_OPTIONS = { link: 'link', more: 'more' } as const;
export const RECEIPT_OPTIONS = { upload: 'upload', without: 'book_without_receipt', private: 'private' } as const;
export const MONEY_IN_OPTIONS = { invoice: 'invoice_payment', sale: 'sale', ownerIn: 'owner_in', other: 'other' } as const;
export const TRANSFER_OPTIONS = { yes: 'yes', no: 'no' } as const;
export const TAX_OPTIONS = { fTaxOwn: 'f_tax', vat: 'vat_payment', unknown: 'unknown', fTax: 'f_tax', other: 'skattekonto_deposit' } as const;
/** "Avser betalningen dessa fakturor?" — confirms or rejects the proposed allocations. */
export const COMBINATION_OPTIONS = { yes: 'combination_yes', no: 'combination_no' } as const;
/** "Är det här en återbetalning av köpet …?" — confirms or rejects the refund. */
export const REFUND_OPTIONS = { yes: 'refund_yes', no: 'refund_no' } as const;

/** "Avser betalningen fakturorna A7 och A9 (Telia)?" */
export function combinationQuestion(labels: readonly string[], counterparty: string): QuestionCard {
  const list = labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} och ${labels[labels.length - 1]}`;
  return {
    kind: 'combination',
    text: `Avser betalningen fakturorna ${list}${counterparty ? ` (${counterparty})` : ''}?`,
    options: [
      { label: labels.length === 2 ? 'Ja, båda' : 'Ja, alla', value: COMBINATION_OPTIONS.yes },
      { label: 'Nej', value: COMBINATION_OPTIONS.no },
    ],
  };
}

/** "Är det här en återbetalning från Clas Ohlson för köpet A12 den 4 mars?" */
export function refundQuestion(merchant: string, label: string, purchaseDate: IsoDate, relativeTo: IsoDate): QuestionCard {
  return {
    kind: 'refund',
    text: `Är det här en återbetalning från ${merchant || 'säljaren'} för köpet ${label || ''}${label ? ' ' : ''}den ${formatSwedishDate(purchaseDate, { relativeTo })}?`,
    options: [
      { label: 'Ja', value: REFUND_OPTIONS.yes },
      { label: 'Nej', value: REFUND_OPTIONS.no },
    ],
  };
}

/** "Hittade ett kvitto som stämmer: {text}, {belopp}, bokfört {datum}. Stämmer det?" */
export function matchQuestion(candidate: Candidate, rowDate: IsoDate): QuestionCard {
  return {
    kind: 'match',
    text: `Hittade ett kvitto som stämmer: ${candidateSummary(candidate, rowDate)}. Stämmer det?`,
    options: [
      { label: 'Ja, koppla', value: MATCH_OPTIONS.link },
      { label: 'Nej, visa fler', value: MATCH_OPTIONS.more },
    ],
  };
}

/** "Har du ett kvitto för det här köpet?" */
export function receiptQuestion(): QuestionCard {
  return {
    kind: 'receipt',
    text: 'Har du ett kvitto för det här köpet?',
    options: [
      { label: 'Ladda upp kvitto', value: RECEIPT_OPTIONS.upload },
      { label: 'Bokför utan kvitto – vi påminner dig', value: RECEIPT_OPTIONS.without },
      { label: 'Det här var privat', value: RECEIPT_OPTIONS.private },
    ],
  };
}

/** "Pengar in: {text}. Vad gäller det?" */
export function moneyInQuestion(text: string): QuestionCard {
  return {
    kind: 'money_in',
    text: `Pengar in: ${text.trim() || 'okänd avsändare'}. Vad gäller det?`,
    options: [
      { label: 'Betalning för en faktura', value: MONEY_IN_OPTIONS.invoice },
      { label: 'Försäljning', value: MONEY_IN_OPTIONS.sale },
      { label: 'Egen insättning', value: MONEY_IN_OPTIONS.ownerIn },
      { label: 'Annat', value: MONEY_IN_OPTIONS.other },
    ],
  };
}

/** "Det här ser ut som en överföring mellan företagets egna konton ({A} → {B}). Stämmer det?" */
export function transferQuestion(fromName: string, toName: string): QuestionCard {
  return {
    kind: 'transfer',
    text: `Det här ser ut som en överföring mellan företagets egna konton (${fromName} → ${toName}). Stämmer det?`,
    options: [
      { label: 'Ja', value: TRANSFER_OPTIONS.yes },
      { label: 'Nej', value: TRANSFER_OPTIONS.no },
    ],
  };
}

/** Skatteverket rows: EF gets the "egna pengar" wording, everyone else the skattekonto one. */
export function taxQuestion(entity: EntityType): QuestionCard {
  switch (entity) {
    case 'enskild_firma':
      return {
        kind: 'tax',
        text: 'Vad var det här?',
        options: [
          { label: 'F-skatt eller egenavgifter – dina egna pengar', value: TAX_OPTIONS.fTaxOwn },
          { label: 'Moms', value: TAX_OPTIONS.vat },
          { label: 'Vet inte', value: TAX_OPTIONS.unknown },
        ],
      };
    case 'aktiebolag':
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return {
        kind: 'tax',
        text: 'Vad var det här?',
        options: [
          { label: 'Moms', value: TAX_OPTIONS.vat },
          { label: 'F-skatt', value: TAX_OPTIONS.fTax },
          { label: 'Annat till skattekontot', value: TAX_OPTIONS.other },
        ],
      };
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/**
 * "Det här var privat" on a company bank row books it against the owner
 * (never ignored). The warning shown per entity; EF has none.
 */
export function privateWarning(entity: EntityType): string | null {
  switch (entity) {
    case 'enskild_firma':
      return null;
    case 'aktiebolag':
      return 'Betala tillbaka till bolaget så snart som möjligt – lån till ägare är inte tillåtna.';
    case 'ideell_forening':
    case 'bostadsrattsforening':
      return 'Utlägg som ska betalas tillbaka.';
    default: {
      const exhaustive: never = entity;
      throw new Error(`Unknown entity type ${String(exhaustive)}`);
    }
  }
}

/** "Bokför utan kvitto" badge and reminder copy. */
export const MISSING_DOCUMENT_BADGE = 'Saknar kvitto';
export const MISSING_DOCUMENT_REMINDER = 'Vi påminner dig – ladda upp kvittot senare så kopplas det automatiskt.';

/** "Egna pengar" follow-up for AB/förening/BRF: "Företaget är skyldigt dig 625 kr …". */
export function ownerOwedText(amountOre: Ore): string {
  return `Företaget är skyldigt dig ${formatSek(amountOre)} kr. Betala tillbaka när du vill – vi föreslår kvittningen när överföringen dyker upp.`;
}

// ---- Choosing the card for a row -----------------------------------------------------

const SKATTEVERKET_TEXT = /skatteverket|skattekonto|skattekontot|skatteverk|skattekto/i;

export function isSkatteverketText(text: string): boolean {
  return SKATTEVERKET_TEXT.test(text);
}

export interface RowQuestionContext {
  entity: EntityType;
  /** Candidates from `rankCandidates`, best first (after `decide` said "ask"). */
  candidates?: readonly Candidate[] | undefined;
  /** Other rows of the company, for the own-transfer pair search. */
  siblingRows?: readonly BankRow[] | undefined;
  /** Payment source names by id, for the transfer wording. */
  sourceNames?: ReadonlyMap<string, string> | undefined;
}

/**
 * The single question for a bank row, in spec priority: match candidate →
 * own transfer → Skatteverket → money in → money out without receipt.
 * Returns null for rows that are not open.
 */
export function questionForRow(row: BankRow, ctx: RowQuestionContext): QuestionCard | null {
  if (row.status !== 'open') return null;
  const top = ctx.candidates?.[0];
  if (top) return matchQuestion(top, row.transactionDate ?? row.bookingDate);
  const pair = ctx.siblingRows ? findTransferPair(row, ctx.siblingRows) : undefined;
  if (pair || looksLikeOwnTransferText(row.text)) {
    const name = (id: string) => ctx.sourceNames?.get(id) ?? 'okänt konto';
    const from = row.amountOre < 0 ? row : pair;
    const to = row.amountOre < 0 ? pair : row;
    return transferQuestion(from ? name(from.paymentSourceId) : 'annat konto', to ? name(to.paymentSourceId) : 'annat konto');
  }
  if (isSkatteverketText(row.text)) return taxQuestion(ctx.entity);
  if (row.amountOre > 0) return moneyInQuestion(row.text);
  return receiptQuestion();
}

/** Map an answer value to the transaction kind the UI should propose next; null = needs the manual editor / another step. */
export function answerToKind(card: QuestionCardKind, value: string): TransactionKind | null {
  switch (card) {
    case 'money_in':
      switch (value) {
        case MONEY_IN_OPTIONS.invoice:
          return 'settle';
        case MONEY_IN_OPTIONS.sale:
          return 'sale';
        case MONEY_IN_OPTIONS.ownerIn:
          return 'owner_in';
        default:
          return null;
      }
    case 'receipt':
      return value === RECEIPT_OPTIONS.private ? 'owner_out' : value === RECEIPT_OPTIONS.without ? 'purchase' : null;
    case 'transfer':
      return value === TRANSFER_OPTIONS.yes ? 'transfer' : null;
    case 'tax':
      return value === TAX_OPTIONS.unknown ? null : 'tax';
    case 'combination':
      return value === COMBINATION_OPTIONS.yes ? 'settle' : null;
    case 'refund':
      return value === REFUND_OPTIONS.yes ? 'refund' : null;
    case 'match':
    case 'private_warning':
      return null;
    default: {
      const exhaustive: never = card;
      throw new Error(`Unknown card ${String(exhaustive)}`);
    }
  }
}

/** Reconciliation strip sentence per statement. */
export function reconciliationText(i: { date: IsoDate; bankOre: Ore; booksOre: Ore }): string {
  const when = formatSwedishDate(i.date, { relativeTo: i.date });
  const base = `Saldo enligt banken ${when}: ${formatSek(i.bankOre)} kr · enligt bokföringen: ${formatSek(i.booksOre)} kr`;
  return i.bankOre === i.booksOre ? `${base} ✓` : `${base} · skillnad ${formatSek(i.bankOre - i.booksOre)} kr`;
}
